import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// ── Clickable column headers, shared by every table that sorts client-side ──────────────────────
// Born in the validation queue (2026-08-03), extracted here when the admin Mapping table and JVID
// registry asked for the same thing (Anouk 2026-08-04: « classement et filtre par champ […] pouvoir
// filtrer par ascendant ou descendant en fonction des titres de la colonne »). One implementation,
// so the three tables can't drift apart.

export type SortDir = 'asc' | 'desc';
/** Drives the FIRST click's direction — text reads best A→Z, dates and numbers biggest-first. */
export type SortKind = 'text' | 'number' | 'date';
export interface TableSort { key: string; dir: SortDir }

export function nextSort(current: TableSort | null, key: string, kind: SortKind = 'text'): TableSort {
  if (current?.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: kind === 'text' ? 'asc' : 'desc' };
}

/**
 * Sort a loaded page of rows. EMPTY VALUES ALWAYS SINK, whatever the direction: a blank cell is not
 * "the smallest", it's "unknown", and keeping it out of the way leaves the meaningful rows on top
 * both ways. `null` sort = the server's own order, untouched.
 */
export function sortRows<T>(
  rows: T[],
  sort: TableSort | null,
  valueOf: (row: T, key: string) => string | number | null | undefined,
): T[] {
  if (!sort) return rows;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = valueOf(a, sort.key);
    const vb = valueOf(b, sort.key);
    const ea = va == null || va === '';
    const eb = vb == null || vb === '';
    if (ea && eb) return 0;
    if (ea) return 1;
    if (eb) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
    // 'fr' + numeric so accents collate properly and "10" lands after "9", not before it.
    return String(va).localeCompare(String(vb), 'fr', { numeric: true, sensitivity: 'base' }) * dir;
  });
}

/** Timestamp for a date column — compared as a NUMBER, never as an ISO string. Lexicographic order
 *  happens to match chronological order for well-formed UTC ISO, but that's a coincidence of format,
 *  not a guarantee; an unparseable value sorts as empty (bottom) instead of silently landing first. */
export function dateValue(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

export function SortableTh({
  label,
  sortKey,
  kind = 'text',
  sort,
  onSort,
  title,
  className,
}: {
  label: string;
  sortKey: string;
  kind?: SortKind;
  sort: TableSort | null;
  onSort: (next: TableSort) => void;
  /** Tooltip on the button — e.g. « Trier sur cette colonne ». */
  title?: string;
  className?: string;
}) {
  const active = sort?.key === sortKey;
  const Icon = active ? (sort!.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      className={cn('px-3 py-3 font-semibold', className)}
      aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <button
        type="button"
        onClick={() => onSort(nextSort(sort, sortKey, kind))}
        title={title}
        className={cn('inline-flex items-center gap-1 hover:text-slate-700', active && 'text-slate-700')}
      >
        {label}
        <Icon className={cn('w-3 h-3', !active && 'text-slate-300')} aria-hidden="true" />
      </button>
    </th>
  );
}
